import { ShowButton, useTable } from "@refinedev/antd";
import {
  getDefaultFilter,
  HttpError,
  IResourceComponentsProps,
  useApiUrl,
  useResource,
  useTranslate
} from "@refinedev/core";
import { useDebounceFn } from "ahooks";
import { Button, DatePicker, notification, Space, Table } from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";

import { CloudDownloadOutlined } from "@ant-design/icons";
import axiosInstance from "axiosInstance";
import { PageList } from "components/pages";
import { DATE_FORMAT } from "config";
import { IUser } from "interfaces";
import { downloadData } from "utils/domHelper";

export const ReportStudentList: React.FC<IResourceComponentsProps> = () => {
  const { resource } = useResource();
  const t = useTranslate();
  const [filterParam, setFilterParam] = useState<any>();

  const { tableProps, searchFormProps, filters } = useTable<IUser, HttpError>({
    resource: "reports/student/lists",
    syncWithLocation: true,
    pagination: {
      mode: "server",
    },
    onSearch: ({ q }: { q: string }) => {
      return [
        {
          field: "q",
          operator: "eq",
          value: q,
        },
      ];
    },
  });
 
  const { form } = searchFormProps;
  const { run: runSearch } = useDebounceFn(() => form?.submit(), {
    wait: 300,
  });
  const apiUrl = useApiUrl();

  const [isRefetchingExport, setIsRefetchingExport] = useState(false);

  const refetchExport = async () => {
    try {
      setIsRefetchingExport(true);
      const { data } = await axiosInstance.get(`${apiUrl}/reports/student/total/export`, {
        responseType: "blob",
        params: {
          date: filterParam?.date,
        },
      });
      downloadData(data, t("users.exports.fileNameReport", {date: filterParam?.date}));
      notification.success({
        message: t("users.notifications.exportReportSuccess"),
      });
    } catch (error: any) {
      notification.error({
        message: error?.message ?? t("users.notifications.exportReportError"),
      });
    } finally {
      setIsRefetchingExport(false);
    }
  };
 
  return (
    <PageList
      searchValue={getDefaultFilter("q", filters, "eq")}
      searchFormProps={searchFormProps}
      onSearch={() => runSearch()}
      actionButtons={[
        <DatePicker
          style={{ marginRight: 8 }}
          placeholder={t("users.placeholders.date")}
          format={DATE_FORMAT}
          value={filterParam?.date ? dayjs(filterParam?.date) : null}
          onChange={(date) => {
            const selectedDate = date ? date.toISOString() : null;
            setFilterParam({
              date: selectedDate
            })
          }}
        />,
        <Button
          icon={<CloudDownloadOutlined />}
          loading={isRefetchingExport}
          disabled={isRefetchingExport||!filterParam?.date}
          onClick={() => refetchExport()}
        >
          {t("buttons.export")}
        </Button>,
      ]}
    >
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="idx" title={t("users.fields.idx")} />
        <Table.Column dataIndex="fullName" title={t("users.fields.fullName")} />
        <Table.Column dataIndex="username" title={t("users.fields.username")} />
        <Table.Column dataIndex="email" title={t("users.fields.email")} />
        <Table.Column dataIndex="classCode" title={t("users.fields.class")} />
        <Table.Column dataIndex="position" title={t("users.fields.position")} />
        <Table.Column
          title={t("users.fields.actions")}
          dataIndex="actions"
          render={(_, record: IUser) => {
            return (
              <Space>
                {resource?.canShow && (
                  <ShowButton hideText size="small" recordItemId={record.id} />
                )}
              </Space>
            );
          }}
        />
      </Table>
    </PageList>
  );
};
